import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import AdminLayout from '../components/AdminLayout';
import api from '../api';
import './AdminBillingOverviewPage.css';

const AdminBillingOverviewPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [overview, setOverview] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [dailySpending, setDailySpending] = useState([]);
    
    // Filtri
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [activeTab, setActiveTab] = useState('overview');
    const [filters, setFilters] = useState({
        sellerId: '',
        transactionType: '',
        serviceType: ''
    });

    useEffect(() => {
        // Imposta date di default (ultimi 30 giorni)
        const today = new Date();
        const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
        
        setEndDate(today.toISOString().split('T')[0]);
        setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    }, []);

    useEffect(() => {
        if (startDate && endDate) {
            loadData();
        }
    }, [startDate, endDate, activeTab, filters]);

    const loadData = async () => {
        if (!startDate || !endDate) return;

        try {
            setLoading(true);
            setError(null);
            
            if (activeTab === 'overview') {
                await loadOverview();
            } else if (activeTab === 'transactions') {
                await loadTransactions();
            } else if (activeTab === 'campaigns') {
                await loadCampaigns();
            } else if (activeTab === 'spending') {
                await loadDailySpending();
            }
            
        } catch (err) {
            setError('Errore nel caricamento dei dati');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadOverview = async () => {
        const response = await api.get('/admin/billing/overview', {
            params: { startDate, endDate }
        });
        setOverview(response.data.data);
    };

    const loadTransactions = async () => {
        const response = await api.get('/admin/billing/wallet-transactions', {
            params: { 
                startDate, 
                endDate, 
                sellerId: filters.sellerId || undefined,
                transactionType: filters.transactionType || undefined
            }
        });
        setTransactions(response.data.data.transactions || []);
    };

    const loadCampaigns = async () => {
        const response = await api.get('/admin/billing/active-campaigns', {
            params: { 
                sellerId: filters.sellerId || undefined
            }
        });
        setCampaigns(response.data.data.campaigns || []);
    };

    const loadDailySpending = async () => {
        const response = await api.get('/admin/billing/daily-spending', {
            params: { 
                startDate, 
                endDate,
                sellerId: filters.sellerId || undefined,
                serviceType: filters.serviceType || undefined
            }
        });
        setDailySpending(response.data.data.daily_costs || []);
    };



    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('it-IT', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('it-IT');
    };

    if (!user || user.role !== 'admin') {
        return <div>Accesso negato</div>;
    }

    return (
        <AdminLayout>
            <div className="admin-billing-overview-page">
                <div className="page-header">
                    <h1>🏦 Overview Billing & Transazioni</h1>
                    <p>Panoramica completa dei pagamenti, transazioni e campagne pubblicitarie</p>
                </div>

                {/* Filtri Globali */}
                <div className="filters-section">
                    <div className="date-filters">
                        <div className="filter-group">
                            <label>Data Inizio:</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>
                        
                        <div className="filter-group">
                            <label>Data Fine:</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>

                        <div className="filter-group">
                            <label>Seller ID:</label>
                            <input
                                type="text"
                                placeholder="ID Seller (opzionale)"
                                value={filters.sellerId}
                                onChange={(e) => setFilters({...filters, sellerId: e.target.value})}
                            />
                        </div>
                    </div>


                </div>

                {/* Tab Navigation */}
                <div className="tab-navigation">
                    <button 
                        className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        📊 Overview
                    </button>
                    <button 
                        className={`tab ${activeTab === 'transactions' ? 'active' : ''}`}
                        onClick={() => setActiveTab('transactions')}
                    >
                        💳 Transazioni
                    </button>
                    <button 
                        className={`tab ${activeTab === 'campaigns' ? 'active' : ''}`}
                        onClick={() => setActiveTab('campaigns')}
                    >
                        🎯 Campagne
                    </button>
                    <button 
                        className={`tab ${activeTab === 'spending' ? 'active' : ''}`}
                        onClick={() => setActiveTab('spending')}
                    >
                        📈 Spesa Giornaliera
                    </button>
                </div>

                {loading && <div className="loading">Caricamento...</div>}
                {error && <div className="error">{error}</div>}

                {/* Overview Tab */}
                {activeTab === 'overview' && overview && (
                    <div className="overview-content">
                        <div className="stats-grid">
                            {/* Portafogli Attivi */}
                            <div className="stat-card">
                                <h3>🏦 Portafogli Attivi</h3>
                                <div className="stat-number">{overview.active_wallets.total_wallets || 0}</div>
                                <div className="stat-details">
                                    <p>Saldo Totale: {formatCurrency(overview.active_wallets.total_balance)}</p>
                                    <p>Saldo Medio: {formatCurrency(overview.active_wallets.avg_balance)}</p>
                                    <p>Auto-ricarica: {overview.active_wallets.auto_recharge_enabled_count || 0}</p>
                                </div>
                            </div>

                            {/* Transazioni Portafoglio */}
                            <div className="stat-card">
                                <h3>💳 Transazioni Portafoglio</h3>
                                {overview.wallet_transactions.map(wt => (
                                    <div key={wt.transaction_type} className="transaction-stat">
                                        <span className="type">{wt.transaction_type}</span>
                                        <span className="count">{wt.count}x</span>
                                        <span className="amount">{formatCurrency(wt.total_amount)}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Pagamenti */}
                            <div className="stat-card">
                                <h3>💰 Pagamenti</h3>
                                {overview.payment_charges.map(pc => (
                                    <div key={`${pc.charge_type}_${pc.status}`} className="payment-stat">
                                        <span className="type">{pc.charge_type}</span>
                                        <span className={`status status-${pc.status}`}>{pc.status}</span>
                                        <span className="amount">{formatCurrency(pc.total_amount)}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Costi Pubblicitari */}
                            <div className="stat-card">
                                <h3>📊 Costi Pubblicitari</h3>
                                {overview.daily_costs.map(dc => (
                                    <div key={dc.service_type} className="cost-stat">
                                        <span className="service">{dc.service_type}</span>
                                        <div className="metrics">
                                            <p>Google: {formatCurrency(dc.total_google_spend)}</p>
                                            <p>Loverlist: {formatCurrency(dc.total_loverlist_revenue)}</p>
                                            <p>Seller: {dc.sellers_count}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Top Seller */}
                        <div className="top-sellers-section">
                            <h3>🏆 Top Seller per Volume</h3>
                            <div className="top-sellers-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Seller</th>
                                            <th>Business</th>
                                            <th>Pagamenti</th>
                                            <th>Volume</th>
                                            <th>Saldo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {overview.top_sellers.map(seller => (
                                            <tr key={seller.seller_id}>
                                                <td>
                                                    <div className="seller-info">
                                                        <span className="email">{seller.email}</span>
                                                        <span className="id">ID: {seller.seller_id}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="business-info">
                                                        <span className="business-name">{seller.business_name || 'N/A'}</span>
                                                        <span className="store-name">{seller.store_name || 'N/A'}</span>
                                                    </div>
                                                </td>
                                                <td>{seller.payments_count}x</td>
                                                <td className="amount">{formatCurrency(seller.total_payments)}</td>
                                                <td className="balance">{formatCurrency(seller.current_balance)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Transactions Tab */}
                {activeTab === 'transactions' && (
                    <div className="transactions-content">
                        <div className="transactions-filters">
                            <select 
                                value={filters.transactionType}
                                onChange={(e) => setFilters({...filters, transactionType: e.target.value})}
                            >
                                <option value="">Tutti i tipi</option>
                                <option value="recharge">Ricarica</option>
                                <option value="deduction">Deduzione</option>
                                <option value="refund">Rimborso</option>
                            </select>
                        </div>

                        <div className="transactions-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Seller</th>
                                        <th>Tipo</th>
                                        <th>Importo</th>
                                        <th>Saldo Prima</th>
                                        <th>Saldo Dopo</th>
                                        <th>Dettagli</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map(transaction => (
                                        <tr key={transaction.id}>
                                            <td>{formatDate(transaction.created_at)}</td>
                                            <td>
                                                <div className="seller-info">
                                                    <span className="email">{transaction.seller_email}</span>
                                                    <span className="business">{transaction.business_name || transaction.store_name}</span>
                                                </div>
                                            </td>
                                            <td className={`type type-${transaction.transaction_type}`}>
                                                {transaction.transaction_type}
                                            </td>
                                            <td className="amount">{formatCurrency(transaction.amount)}</td>
                                            <td className="balance">{formatCurrency(transaction.balance_before)}</td>
                                            <td className="balance">{formatCurrency(transaction.balance_after)}</td>
                                            <td className="details">
                                                {transaction.charge_type && <span className="tag">{transaction.charge_type}</span>}
                                                {transaction.service_type && <span className="tag">{transaction.service_type}</span>}
                                                {transaction.campaign_name && <span className="campaign">{transaction.campaign_name}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Campaigns Tab */}
                {activeTab === 'campaigns' && (
                    <div className="campaigns-content">
                        <div className="campaigns-stats">
                            <div className="stat">
                                <span className="label">Campagne Attive:</span>
                                <span className="value">{campaigns.length}</span>
                            </div>
                            <div className="stat">
                                <span className="label">Budget Totale:</span>
                                <span className="value">{formatCurrency(campaigns.reduce((sum, c) => sum + parseFloat(c.budget || 0), 0))}</span>
                            </div>
                            <div className="stat">
                                <span className="label">Saldo Totale:</span>
                                <span className="value">{formatCurrency(campaigns.reduce((sum, c) => sum + parseFloat(c.current_balance || 0), 0))}</span>
                            </div>
                        </div>

                        <div className="campaigns-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Campagna</th>
                                        <th>Seller</th>
                                        <th>Budget/giorno</th>
                                        <th>Saldo Portafoglio</th>
                                        <th>Spesa 7gg</th>
                                        <th>Spesa Totale</th>
                                        <th>Performance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {campaigns.map(campaign => (
                                        <tr key={campaign.id}>
                                            <td>
                                                <div className="campaign-info">
                                                    <span className="name">{campaign.campaign_name}</span>
                                                    <span className="status">{campaign.status}</span>
                                                    <span className="id">ID: {campaign.google_ads_campaign_id}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="seller-info">
                                                    <span className="email">{campaign.seller_email}</span>
                                                    <span className="business">{campaign.business_name || campaign.store_name}</span>
                                                </div>
                                            </td>
                                            <td className="budget">{formatCurrency(campaign.budget)}</td>
                                            <td className="balance">{formatCurrency(campaign.current_balance)}</td>
                                            <td className="spent">{formatCurrency(campaign.spent_7_days)}</td>
                                            <td className="total-spent">{formatCurrency(campaign.total_spent)}</td>
                                            <td className="performance">
                                                <div className="metrics">
                                                    <span>👁️ {campaign.total_impressions?.toLocaleString() || 0}</span>
                                                    <span>👆 {campaign.total_clicks?.toLocaleString() || 0}</span>
                                                    <span>🎯 {campaign.active_days_7 || 0}/7 giorni</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Daily Spending Tab */}
                {activeTab === 'spending' && (
                    <div className="spending-content">
                        <div className="spending-filters">
                            <select 
                                value={filters.serviceType}
                                onChange={(e) => setFilters({...filters, serviceType: e.target.value})}
                            >
                                <option value="">Tutti i servizi</option>
                                <option value="google_ads_loverlist">Google Ads Loverlist</option>
                                <option value="google_ads_personal">Google Ads Personal</option>
                                <option value="loverlist_cpc">Loverlist CPC</option>
                                <option value="loverlist_impressions">Loverlist Impressions</option>
                            </select>
                        </div>

                        <div className="spending-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Seller</th>
                                        <th>Servizio</th>
                                        <th>Campagna</th>
                                        <th>Spesa Google</th>
                                        <th>Revenue Loverlist</th>
                                        <th>Metriche</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dailySpending.map(spending => (
                                        <tr key={spending.id}>
                                            <td>{formatDate(spending.cost_date)}</td>
                                            <td>
                                                <div className="seller-info">
                                                    <span className="email">{spending.seller_email}</span>
                                                    <span className="business">{spending.business_name || spending.store_name}</span>
                                                </div>
                                            </td>
                                            <td className={`service service-${spending.service_type}`}>
                                                {spending.service_type}
                                            </td>
                                            <td className="campaign">
                                                <div className="campaign-info">
                                                    <span className="name">{spending.campaign_name || 'N/A'}</span>
                                                    <span className="id">{spending.campaign_id}</span>
                                                </div>
                                            </td>
                                            <td className="google-spend">{formatCurrency(spending.service_spend_amount)}</td>
                                            <td className="loverlist-revenue">{formatCurrency(spending.loverlist_cost_amount)}</td>
                                            <td className="metrics">
                                                <span>👁️ {spending.impressions?.toLocaleString() || 0}</span>
                                                <span>👆 {spending.clicks?.toLocaleString() || 0}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default AdminBillingOverviewPage;
